import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import OptionCard from './OptionCard'

const EXCLUSIVE_SELECTION_ID = 'none'
const MYCYBER_URL = import.meta.env.VITE_MYCYBER_URL || 'https://myciber.gov.pt'

function selectedFromQuestion(questionValue) {
  return Array.isArray(questionValue?.selectedIds) ? questionValue.selectedIds : []
}

export default function SectorCollectionQuestion({ question, disabled, onSubmit, onReturnHome }) {
  const { t } = useTranslation()
  const value = useMemo(() => question?.value || {}, [question])
  const groups = useMemo(() => value.groups || [], [value])
  const [selectedIds, setSelectedIds] = useState(() => selectedFromQuestion(value))
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    setSelectedIds(selectedFromQuestion(value))
  }, [value])

  useEffect(() => {
    if (!selectedIds.includes(EXCLUSIVE_SELECTION_ID)) {
      setDialogOpen(false)
    }
  }, [selectedIds])

  function toggleSelection(selectionId) {
    setSelectedIds((current) => {
      if (selectionId === EXCLUSIVE_SELECTION_ID) {
        return current.includes(EXCLUSIVE_SELECTION_ID) ? [] : [EXCLUSIVE_SELECTION_ID]
      }

      const withoutExclusive = current.filter((item) => item !== EXCLUSIVE_SELECTION_ID)
      return withoutExclusive.includes(selectionId)
        ? withoutExclusive.filter((item) => item !== selectionId)
        : [...withoutExclusive, selectionId]
    })
  }

  function handleContinue() {
    if (selectedIds.includes(EXCLUSIVE_SELECTION_ID)) {
      setDialogOpen(true)
      return
    }

    onSubmit({ action: 'replace', selectionIds: selectedIds })
  }

  function handleOpenMyCyber() {
    window.open(MYCYBER_URL, '_blank', 'noopener,noreferrer')
    onReturnHome?.()
  }

  return (
    <>
      <Stack spacing={3}>
        {groups.map((group) => (
          <Stack key={group.id} spacing={1.25}>
            <Typography variant="subtitle2" color="text.secondary">
              {group.label}
            </Typography>
            <Stack spacing={1}>{(group.options || []).map((option, index) => (
              <OptionCard
                key={option.id}
                option={option}
                index={index}
                selected={selectedIds.includes(option.id)}
                disabled={disabled}
                multi
                onClick={() => toggleSelection(option.id)}
              />
            ))}</Stack>
          </Stack>
        ))}

        <Box flexGrow={1} />

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5}>
          <Button
            size="large"
            color="primary"
            disabled={disabled || selectedIds.length === 0}
            onClick={handleContinue}
          >
            {t('questionnaire.continue')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('questionnaire.sectorCollection.multiHelp')}
          </Typography>
        </Stack>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('questionnaire.sectorCollection.outOfScopeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('questionnaire.sectorCollection.outOfScopeMessage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={onReturnHome}>
            {t('questionnaire.sectorCollection.returnHome')}
          </Button>
          <Button variant="contained" onClick={handleOpenMyCyber} color="primary">
            {t('questionnaire.sectorCollection.openMyCyber')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
